import { Button, Spin, Tag } from "antd";
import {
  AppWindow,
  Calculator,
  ChevronRight,
  FolderOpen,
  LayoutDashboard,
  Lock,
  ShoppingBag,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { collectMenuTreeNodeIds, PermissionTree, usePermission } from "@/components/PermissionTree";
import { cn } from "@/lib/cn";

import type { ResolvedRoleTerminal, RoleDetailViewModel } from "./types";
import { useRoleMenuIds } from "./useRoleMenuIds";

/** 系统端编码 → 图标 */
const SYSTEM_TYPE_ICON: Record<string, React.ReactNode> = {
  BIZ: <ShoppingBag className="w-4 h-4" />,
  POS: <Calculator className="w-4 h-4" />,
  WKS: <LayoutDashboard className="w-4 h-4" />,
  OPS: <AppWindow className="w-4 h-4" />,
};

/** 单端权限面板 props */
interface RoleTerminalPanelProps {
  /** 端绑定信息 */
  terminal: ResolvedRoleTerminal;
  /** 是否展开 */
  isExpanded: boolean;
  /** 切换展开 */
  onToggle: () => void;
  /** 加载后无权限树时回调，用于隐藏该端 */
  onEmpty: (systemTypeId: string) => void;
}

/**
 * 单端权限折叠面板：仅外层折叠 + PermissionTree，避免 AppPermissionSection 重复套层
 */
const RoleTerminalPanel = ({ terminal, isExpanded, onToggle, onEmpty }: RoleTerminalPanelProps) => {
  const { menuIds, loading: menuIdsLoading } = useRoleMenuIds(terminal.roleId);
  const {
    loading: groupsLoading,
    groups,
    selectedIds,
    expandedIds,
    handleToggleExpand,
    handleTogglePermission,
  } = usePermission({
    params: {
      roleId: terminal.roleId,
      orgId: terminal.orgId,
      orgType: terminal.orgType ?? terminal.templateType,
    },
    value: menuIds,
    readOnly: true,
  });

  const loading = groupsLoading || menuIdsLoading;

  /** 当前端对应的菜单树 */
  const group = groups.find((item) => item.systemTypeId === terminal.systemTypeId) ?? groups[0];
  const treeNodes = group?.menuTree ?? [];
  /** 该端菜单总数与已选数 */
  const appMenuIds = collectMenuTreeNodeIds(treeNodes);
  const selectedCount = selectedIds.filter((id) => appMenuIds.includes(id)).length;

  /** 菜单树加载完成且无权限时不展示该端 */
  useEffect(() => {
    if (!loading && treeNodes.length === 0) {
      onEmpty(terminal.systemTypeId);
    }
  }, [loading, onEmpty, terminal.systemTypeId, treeNodes.length]);

  if (!loading && treeNodes.length === 0) return null;
  if (!loading && appMenuIds.length === 0 && (terminal.permissionCount ?? 0) === 0) return null;

  const icon = SYSTEM_TYPE_ICON[terminal.systemTypeCode] ?? <AppWindow className="w-4 h-4" />;

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200">
      <button
        type="button"
        className="w-full px-4 py-2.5 bg-slate-50/80 flex items-center justify-between hover:bg-slate-100/80 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <ChevronRight
            className={cn("w-4 h-4 text-slate-400 transition-transform", isExpanded && "rotate-90")}
          />
          {icon}
          <span className="text-sm font-medium">{terminal.systemTypeName}</span>
          <Tag bordered className="text-[10px] m-0">
            {terminal.systemTypeCode}
          </Tag>
        </div>
        <Tag bordered={false} className="text-[10px] m-0">
          {selectedCount}/{appMenuIds.length || terminal.permissionCount || 0}
        </Tag>
      </button>
      {isExpanded ? (
        <div className="px-4 py-3 border-t">
          {loading ? (
            <div className="flex justify-center py-6">
              <Spin size="small" />
            </div>
          ) : (
            <PermissionTree
              readOnly
              nodes={treeNodes}
              selectedPermissions={selectedIds}
              expandedIds={expandedIds}
              onToggleExpand={handleToggleExpand}
              onToggle={handleTogglePermission}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

/** 角色权限树展示区 props */
export interface RolePermissionViewProps {
  /** 角色聚合数据 */
  data: RoleDetailViewModel;
}

/**
 * 角色权限配置只读展示：按端折叠 + PermissionTree
 */
export const RolePermissionView = ({ data }: RolePermissionViewProps) => {
  /** 加载后确认无权限的端，不再展示 */
  const [hiddenSystemTypeIds, setHiddenSystemTypeIds] = useState<Set<string>>(new Set());
  /** 各端展开状态，key 为 systemTypeId */
  const [expandedApps, setExpandedApps] = useState<Record<string, boolean>>({});

  /** 候选端：permissionCount > 0，且未被标记为空 */
  const candidateTerminals = useMemo(
    () =>
      data.terminals.filter(
        (item) => (item.permissionCount ?? 0) > 0 && !hiddenSystemTypeIds.has(item.systemTypeId),
      ),
    [data.terminals, hiddenSystemTypeIds],
  );

  /** terminals 变化时重置隐藏状态 */
  useEffect(() => {
    setHiddenSystemTypeIds(new Set());
    setExpandedApps(Object.fromEntries(data.terminals.map((item) => [item.systemTypeId, true])));
  }, [data.terminals]);

  /** 是否全部展开 */
  const allExpanded = useMemo(
    () => candidateTerminals.every((item) => expandedApps[item.systemTypeId] !== false),
    [candidateTerminals, expandedApps],
  );

  /** 标记无权限端并隐藏 */
  const handleTerminalEmpty = useCallback((systemTypeId: string) => {
    setHiddenSystemTypeIds((prev) => {
      if (prev.has(systemTypeId)) return prev;
      const next = new Set(prev);
      next.add(systemTypeId);
      return next;
    });
  }, []);

  /** 切换单端展开 */
  const toggleApp = useCallback((systemTypeId: string) => {
    setExpandedApps((prev) => ({ ...prev, [systemTypeId]: !prev[systemTypeId] }));
  }, []);

  /** 全部展开 / 收起 */
  const toggleAll = useCallback(() => {
    const next = !allExpanded;
    setExpandedApps(
      Object.fromEntries(candidateTerminals.map((item) => [item.systemTypeId, next])),
    );
  }, [allExpanded, candidateTerminals]);

  if (candidateTerminals.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500 text-sm">
        <FolderOpen className="h-10 w-10 mx-auto mb-2 opacity-30" />
        <p>暂无该角色的权限配置信息</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-500 flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5" />
          权限配置
        </h4>
        <Button type="text" size="small" className="text-xs h-6 px-2" onClick={toggleAll}>
          {allExpanded ? "全部收起" : "全部展开"}
        </Button>
      </div>
      {candidateTerminals.map((terminal) => (
        <RoleTerminalPanel
          key={`${terminal.systemTypeId}-${terminal.roleId}`}
          terminal={terminal}
          isExpanded={expandedApps[terminal.systemTypeId] !== false}
          onToggle={() => toggleApp(terminal.systemTypeId)}
          onEmpty={handleTerminalEmpty}
        />
      ))}
    </div>
  );
};

/** 权限区加载占位 */
export const RolePermissionLoading = () => (
  <div className="flex justify-center py-10">
    <Spin tip="加载角色权限..." />
  </div>
);
